/**
 * Calculator Input Builder
 * Assembles the input object that dashboard calculators expect,
 * given a user's data and client configuration.
 *
 * Extracted from index.js getEquityData() for reuse in
 * custom reports and cap table columns.
 */

import { isUserTransaction, getActiveUserIds, getEffectiveStrikePriceForUser, calculateVestedFromSchedule } from './equity-calculation-utils.js';
import { getEffectiveDenomination } from './denomination-resolver.js';
import { computeCAGRAnchors } from './cagr-utils.js';
import { isPastOrDateless } from './event-utils.js';
import { resolveClassTotalShares } from './class-aggregates.js';
export { computeCAGRAnchors, attachCAGR } from './cagr-utils.js';

/**
 * Aggregate user events against client events to compute
 * bases, distributions, contributions, returnsOfCapital, and incomeAllocations
 * per share class.
 *
 * @param {Object} userShares - userClient.shares object (keyed by class)
 * @param {Object} equity - clientData.equity object (keyed by class)
 * @param {string[]} shareClasses - Array of share class keys
 * @param {string|string[]} userIds - User ID(s) for transaction filtering
 * @returns {Object} { bases, distributions, pendingDistributions, contributions, returnsOfCapital, incomeAllocations }
 */
/**
 * Compute net transfer amounts per share class from transactions.
 * @param {Object} equity - clientData.equity
 * @param {string[]} shareClasses - Array of share class keys
 * @param {string|string[]} userIds - User ID(s) for filtering
 * @returns {Object} Net transfers keyed by share class (positive = received, negative = sent)
 */
export function computeNetTransfers(equity, shareClasses, userIds) {
	const transfers = {};
	const normalizedIds = (Array.isArray(userIds) ? userIds : [userIds]).map((id) => id?.toLowerCase()).filter(Boolean);

	shareClasses.forEach((c) => {
		transfers[c] = 0;
		if (equity[c]?.transactions) {
			// #6279: shares-denominated classes (e.g. ZEPH) record a per-transaction
			// dollar tax basis independent of qty * sharePrice -- gift/basis-only
			// transfers carry sharePrice: 0, so the qty-or-qty*price fallback below
			// (written for dollar-denominated LLC transfers, where quantity IS the
			// dollar amount) misreads the share quantity itself as dollars. Prefer
			// the transaction's own taxBasis, falling back to qty * sharePrice for
			// an arms-length purchase/sale with no tax-basis override recorded --
			// same priority gift-transfer.js's resolveEffectiveTaxBasis() already
			// uses, and the same distinction ReportEngine's applyTransactionTaxBasis
			// flag makes for ZEPH's own report.
			const isShares = equity[c]?.denomination === 'shares';
			equity[c].transactions.forEach((t) => {
				const senderLower = t.sender?.toLowerCase();
				// A transaction with no sender is an original issuance/opening-basis
				// record (e.g. ZEPH's "Tax Plug Block" rows, senderName: "Issuer"), not
				// a peer-to-peer transfer -- it's already counted once via the class's
				// Contribution/"Opening Tax Basis" event. Treating it as a transfer here
				// too double-counts that same basis into netTransfers.
				if (!senderLower) return;
				const qty = Number(t.quantity) || 0;
				const price = Number(t.sharePrice) || 0;
				const txBasis = Number(t.taxBasis) || 0;
				let amount;
				if (isShares) {
					amount = txBasis || qty * price;
				} else {
					// quantity × sharePrice for equity transfers; fall back to quantity alone
					// for fund capital transfers where quantity IS the dollar amount (sharePrice omitted)
					amount = price > 0 ? qty * price : qty;
				}
				if (!amount) return;
				const receiverLower = t.receiver?.toLowerCase();
				const isSender = normalizedIds.includes(senderLower);
				const isReceiver = receiverLower && normalizedIds.includes(receiverLower);
				if (isReceiver && !isSender) transfers[c] += amount;
				else if (isSender && !isReceiver) transfers[c] -= amount;
			});
		}
	});
	return transfers;
}

export function aggregateEventData(userShares, equity, shareClasses, userIds) {
	const bases = {};
	const distributions = {};
	const pendingDistributions = {}; // #5242: future-dated Distribution/Cash Dividend events
	const contributions = {};
	const returnsOfCapital = {};
	const incomeAllocations = {};
	const accruals = {};
	const transfers = computeNetTransfers(equity, shareClasses, userIds);

	let userEvents = [];
	let clientEvents = [];
	let futureClientEvents = [];
	const nowTs = Math.floor(Date.now() / 1000);

	shareClasses.forEach((c) => {
		bases[c] = -1;
		distributions[c] = 0;
		pendingDistributions[c] = 0;
		contributions[c] = 0;
		returnsOfCapital[c] = 0;
		incomeAllocations[c] = 0;
		accruals[c] = 0;

		if (userShares[c]?.events) {
			userShares[c].events.forEach((e) => userEvents.push(e));
		}
		if (equity[c]?.events) {
			equity[c].events.forEach((e) => {
				if (isPastOrDateless(e, nowTs)) {
					clientEvents.push({ ...e, shareClass: c });
				} else {
					futureClientEvents.push({ ...e, shareClass: c });
				}
			});
		}
	});

	// Process transactions to compute transaction-derived basis.
	// Mirrors the core path in index.js:getEquityData (9705-9750). Required for
	// transaction-driven clients (EXTERNAL_DATA, e.g. PharmaJet) where paid-in
	// capital comes from transactions rather than Contribution events.
	// Runs BEFORE the event loop so the event loop's `bases[sc] == -1` guard
	// only fires for classes that neither transactions nor events touched.
	// Client-specific edge cases (isJMS no-sale-basis, useStrike step-down,
	// NO_BASIS strike addition) are intentionally omitted — they only affect
	// a handful of clients and can be added as options later if preview
	// pipelines need them.
	const normalizedIds = (Array.isArray(userIds) ? userIds : [userIds]).map((id) => id?.toLowerCase()).filter(Boolean);

	shareClasses.forEach((c) => {
		if (!equity[c]?.transactions) return;
		equity[c].transactions.forEach((t) => {
			if (t.notes?.toLowerCase().includes('gift')) return;
			const sender = t.sender?.toLowerCase();
			const receiver = t.receiver?.toLowerCase();
			const isSender = sender && normalizedIds.includes(sender);
			const isReceiver = !isSender && receiver && normalizedIds.includes(receiver);
			if (!isSender && !isReceiver) return;

			if (bases[c] === -1) bases[c] = 0;
			const dollarAmount = (Number(t.quantity) || 0) * (Number(t.sharePrice) || 0);
			if (isSender) {
				if (!t.strikePrice || t.strikePrice <= 0) bases[c] -= dollarAmount;
			} else {
				bases[c] += dollarAmount;
			}
		});
	});

	// Process events to compute bases and aggregations
	userEvents.forEach((e) => {
		const clientEvent = clientEvents.find((obj) => obj.nrid == e.nrid);
		if (!clientEvent) {
			// #5242: this event's clientEvent definition is future-dated (excluded from
			// clientEvents above) -- track future Distribution/Cash Dividend amounts
			// separately so headlines can show a "pending" total.
			const futureEvent = futureClientEvents.find((obj) => obj.nrid == e.nrid);
			if (futureEvent && (futureEvent.eventType === 'Distribution' || futureEvent.eventType === 'Cash Dividend')) {
				pendingDistributions[futureEvent.shareClass] += Number(e.grossDollars) || 0;
			}
			return;
		}

		const grossDollars = Number(e.grossDollars);
		const sc = clientEvent.shareClass;
		if (bases[sc] == -1 && ['Contribution', 'Redemption', 'Return of Capital'].includes(clientEvent.eventType)) {
			bases[sc] = 0;
		}
		const includeClassROC = equity[sc]?.includeROC;

		switch (clientEvent.eventType) {
			case 'Distribution':
			case 'Cash Dividend':
				distributions[sc] += grossDollars;
				break;
			case 'Contribution':
				bases[sc] += grossDollars;
				contributions[sc] += grossDollars;
				break;
			case 'Redemption':
				bases[sc] -= grossDollars;
				break;
			case 'Return of Capital':
				if (includeClassROC !== false) {
					bases[sc] -= grossDollars;
				}
				returnsOfCapital[sc] += grossDollars;
				break;
			case 'Gain (Loss) Accrual':
				accruals[sc] += grossDollars;
				break;
		}
	});

	return { bases, distributions, pendingDistributions, contributions, returnsOfCapital, incomeAllocations, accruals, transfers };
}

/**
 * Get the latest share price from a class's valuations array.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @returns {{ latest: number, prior: number }} Latest and prior share prices
 */
function getSharePrices(equityClass) {
	if (!equityClass?.valuations?.length) return { latest: 0, prior: 0 };
	const sorted = equityClass.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
	return {
		latest: Number(sorted[0]?.sharePrice) || 0,
		prior: Number(sorted[1]?.sharePrice) || 0,
	};
}

/**
 * Calculate total fund contributions and distributions from client-level events.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @returns {{ fundTotalContributions: number, fundTotalDistributions: number }}
 */
function getFundEventTotals(equityClass) {
	let fundTotalContributions = 0;
	let fundTotalDistributions = 0;
	if (!equityClass?.events?.length) return { fundTotalContributions, fundTotalDistributions };

	equityClass.events.forEach((event) => {
		if (event.eventType === 'Contribution') {
			fundTotalContributions += Number(event.grossDollars) || 0;
		} else if (event.eventType === 'Distribution') {
			fundTotalDistributions += Number(event.grossDollars) || 0;
		}
	});
	return { fundTotalContributions, fundTotalDistributions };
}

/**
 * Calculate totalTarget from a vesting object (BW-style).
 * Sums schedule amounts and goal amounts, skipping cashedOut/forfeited entries.
 * @param {Object} vestingObj - User's vesting object for a class
 * @returns {number} Total target shares (0 if no vesting)
 */
function getTotalTarget(vestingObj) {
	if (!vestingObj || typeof vestingObj !== 'object') return 0;
	let totalTarget = 0;
	Object.values(vestingObj).forEach((entry) => {
		if (entry?.cashedOut === true) return;
		if (entry?.schedule) {
			Object.values(entry.schedule).forEach((item) => {
				if (item?.forfeited === true) return;
				totalTarget += Number(item?.amount || 0);
			});
		}
		if (entry?.goals) {
			Object.values(entry.goals).forEach((goal) => {
				if (goal?.forfeited === true) return;
				totalTarget += Number(goal?.amount || 0);
			});
		}
	});
	return totalTarget;
}

/**
 * Find the underlying stock price for option-type classes without their own valuations.
 * @param {Object} equityClass - clientData.equity[shareClass]
 * @param {Object} allEquity - clientData.equity (all classes)
 * @returns {number} Share price from underlying class, or 0
 */
function getUnderlyingSharePrice(equityClass, allEquity) {
	// First try explicit underlyingClass if defined
	let underlyingEquity = equityClass?.underlyingClass ? allEquity[equityClass.underlyingClass] : null;

	// Fallback: find first share class with valuations that isn't an option type
	if (!underlyingEquity?.valuations?.length) {
		const stockClasses = Object.keys(allEquity).filter((cls) => {
			const eq = allEquity[cls];
			return eq?.valuations?.length > 0 && !eq?.useStrike;
		});
		if (stockClasses.length > 0) {
			underlyingEquity = allEquity[stockClasses[0]];
		}
	}

	if (!underlyingEquity?.valuations?.length) return 0;
	const sorted = underlyingEquity.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
	return Number(sorted[0]?.sharePrice) || 0;
}

/**
 * Convert TVPI multipliers to raw dollar NAV for fund classes.
 * TVPI = (NAV + Distributions) / Contributions, so NAV = TVPI * Contributions - Distributions
 *
 * @param {Object} holdings - The calculator input holdings object (mutated in place)
 * @param {string[]} shareClasses - Share class keys
 * @param {Object} equity - clientData.equity
 */
function convertTvpiToNav(holdings, shareClasses, equity) {
	shareClasses.forEach((k) => {
		const tvpi = holdings.fundNav[k] || 0;
		const fundTotal = holdings.fundTotalContributions[k] || 0;
		if (tvpi > 0 && fundTotal > 0) {
			const keyEquity = equity[k];
			let valDate = Infinity;
			if (keyEquity?.valuations?.length) {
				const sorted = keyEquity.valuations.slice().sort((a, b) => Number(b.date) - Number(a.date));
				valDate = Number(sorted[0].date) || Infinity;
			}
			// Only include distributions through the valuation date
			let fundDist = 0;
			if (keyEquity?.events?.length) {
				keyEquity.events.forEach((event) => {
					if (event.eventType === 'Distribution' && Number(event.date) <= valDate) {
						fundDist += Number(event.grossDollars) || 0;
					}
				});
			}
			holdings.fundNav[k] = tvpi * fundTotal - fundDist;
		}
	});
}

/**
 * Detect if the client uses TVPI-style valuations (where sharePrice is a multiplier, not dollars).
 * @param {Object} clientData - Client configuration
 * @returns {boolean}
 */
export function isTvpiValuation(clientData) {
	const labelValuation = clientData?.language?.label_valuation || '';
	const labelTvpi = clientData?.language?.label_tvpi || '';
	return labelValuation.startsWith('%') || labelValuation.toUpperCase().includes('TVPI') || labelTvpi.toUpperCase().includes('TVPI');
}

/**
 * Per-client calculator option overrides, keyed by client slug (mirrors the
 * hostname-derived boolean flags in helpers/client-flags.js, e.g. isSEQ/isBW/
 * isUA/isCER). Kept here — not imported from client-flags.js — because that
 * module resolves the client slug from `window.location`/cookies at import
 * time and throws outside a browser (breaks Node-based unit tests). Both the
 * live dashboard (index.js) and the admin shareholder preview
 * (preview-shareholder.js) call this so a per-client quirk added to one path
 * can't silently drift out of sync with the other. See #5836.
 *
 * @param {string} client - Client slug (e.g. "sequoia-financial")
 * @returns {Object} options object suitable for buildCalculatorInput()
 */
export function getClientCalculatorOptions(client) {
	return {
		floorAtZero: client === 'sequoia-financial',
		zeroStrikePrice: client === 'barry-wehmiller',
		clientFlags: {
			isUA: client === 'urbanalpha',
			isCER: client === 'ceritypartners',
		},
	};
}

/**
 * Build the calculator input object for a single user.
 * This produces the same shape consumed by all dashboard calculators
 * (PrivateCompany409A, FundInvestor, ContributionsAccruals, etc.)
 *
 * @param {Object} params
 * @param {Object} params.userShares - userClient.shares object (keyed by class)
 * @param {Object} params.clientData - Full client configuration
 * @param {string[]} params.shareClasses - Array of share class keys to include
 * @param {string|string[]} params.userIds - User ID(s) for transaction/event filtering
 * @param {Object} [params.eventData] - Pre-computed event aggregations (if already available).
 *   If omitted, will be computed via aggregateEventData().
 *   Shape: { bases, distributions, contributions, returnsOfCapital, incomeAllocations }
 * @param {Object} [params.options] - Calculator options overrides
 * @param {boolean} [params.options.floorAtZero=false] - Floor negative values at zero
 * @param {number} [params.options.percentagePrecision] - Decimal precision for fund %
 * @param {boolean} [params.options.zeroStrikePrice=false] - Force strike price to 0 (BW-style)
 * @param {Object} [params.options.clientFlags] - Legacy client-flag inputs that affect holdings
 *   assembly. Kept here (rather than in calculator code) so calculators stay
 *   agnostic to client identity.
 * @param {boolean} [params.options.clientFlags.isUA] - Urban Alpha: exclude share
 *   classes tagged `SOLD` from paid-in-capital aggregation (basis becomes the -1
 *   sentinel, so calculators' totalBasis sum skips them). Current value still
 *   includes them — the asymmetry matches the pre-#5198 render-path behavior at
 *   index.js:11870-11876.
 * @param {boolean} [params.options.clientFlags.isCER] - Cerity Partners: CAGR end
 *   date uses the last matched book-up event date rather than Date.now(). Sets
 *   holdings.cagrOptions to disable the useCurrentTime defaults in attachCAGR.
 * @param {Array} [params.capTableData] - Full cap table array. Optional; enables
 *   resolveClassTotalShares()'s live-sum fallback tier for callers that already
 *   have it loaded (admin/preview contexts). Never required to be fetched just
 *   for this -- non-admin sessions correctly omit it (see class-aggregates.js).
 * @param {string} [params.client] - Client slug, paired with capTableData for the
 *   same fallback tier above.
 * @returns {Object} Calculator input object
 */
export function buildCalculatorInput({ userShares, clientData, shareClasses, userIds, eventData, options = {}, capTableData, client }) {
	if (!userShares || !clientData?.equity || !shareClasses?.length) {
		return null;
	}

	// Compute event aggregations if not provided
	const events = eventData || aggregateEventData(userShares, clientData.equity, shareClasses, userIds);

	// Compute transfers if not already in event data (pre-computed eventData from index.js omits them)
	if (!events.transfers) {
		events.transfers = computeNetTransfers(clientData.equity, shareClasses, userIds);
	}

	// Client-flag driven exclusions. Computed here (not in calculators) so
	// calculators stay agnostic to client identity. Each entry marks a share
	// class that should not contribute to totalPaidInCapital aggregation, but
	// whose per-class basis stays populated (equity cards / reports still
	// surface the raw basis for display).
	const excludeFromPaidInCapital = {};
	const clientFlags = options.clientFlags || {};
	if (clientFlags.isUA) {
		shareClasses.forEach((k) => {
			const firstTag = clientData.equity[k]?.tags?.[0]?.value;
			if (typeof firstTag === 'string' && firstTag.toUpperCase() === 'SOLD') {
				excludeFromPaidInCapital[k] = true;
			}
		});
	}

	const holdings = {
		owned: {},
		vested: {},
		scheduleVested: {},
		totalTarget: {},
		sharePrice: {},
		priorSharePrice: {},
		strikePrice: {},
		tvpi: {},
		fundNav: {},
		fundTotalContributions: {},
		fundTotalDistributions: {},
		totalClassOwned: {},
		denomination: {},
		contributions: events.contributions,
		distributions: events.distributions,
		capitalAccount: events.bases,
		returnsOfCapital: events.returnsOfCapital,
		accruals: events.accruals,
		transfers: events.transfers || {},
		bases: events.bases,
		incomeAllocations: events.incomeAllocations,
		excludeFromPaidInCapital: excludeFromPaidInCapital,
		// CAGR anchor data (first matched event per class + portfolio-level
		// earliest/latest). Calculators consume via attachCAGR() with their own
		// cagrOptions semantic. See #5198 Phase 4.
		cagrAnchors: computeCAGRAnchors(userShares, clientData.equity, shareClasses),
		// Per-client CAGR option overrides merged into calculator's static cagrOptions
		// at attachCAGR call time. Empty object = no override (all non-CER clients).
		cagrOptions: clientFlags.isCER ? { useCurrentTimeForClassCagr: false, useCurrentTimeForPortfolioCagr: false } : {},
	};

	// Populate per-class data
	shareClasses.forEach((k) => {
		const classShares = userShares[k];
		const owned = Number(classShares?.owned || 0);
		holdings.owned[k] = owned;

		// Vested shares. scheduleVested is date/flag-driven and independent of
		// owned — holdings.vested caps it at owned (can't value more than is
		// currently held), but #6266: that cap re-reads as "not yet vested" once
		// a redemption drops owned back to 0, wrongly reviving cleared payroll-
		// withholding commitments. holdings.scheduleVested keeps the uncapped
		// figure for consumers (unvested share/value calcs) that must stay
		// cleared once shares have vested, regardless of later redemption.
		const scheduleVested = classShares?.vesting ? calculateVestedFromSchedule(classShares.vesting, owned) : Number(classShares?.approved ?? owned);
		holdings.vested[k] = Math.min(scheduleVested, owned);
		holdings.scheduleVested[k] = scheduleVested;

		// Total target (BW-style vesting)
		if (classShares?.vesting) {
			const totalTarget = getTotalTarget(classShares.vesting);
			if (totalTarget > 0) {
				holdings.totalTarget[k] = totalTarget;
			}
		}

		const equityClass = clientData.equity[k];
		const hasStrikePriceType = equityClass?.useStrike === true;

		// Share prices from valuations
		const prices = getSharePrices(equityClass);
		if (prices.latest > 0) {
			holdings.sharePrice[k] = prices.latest;
			holdings.tvpi[k] = prices.latest;
			holdings.fundNav[k] = prices.latest;
			if (prices.prior > 0) {
				holdings.priorSharePrice[k] = prices.prior;
			}
		}

		// For options without their own valuations, use underlying stock price
		if (hasStrikePriceType && !holdings.sharePrice[k]) {
			const underlyingPrice = getUnderlyingSharePrice(equityClass, clientData.equity);
			if (underlyingPrice > 0) {
				holdings.sharePrice[k] = underlyingPrice;
			}
		}

		// Fund event totals
		const fundTotals = getFundEventTotals(equityClass);
		holdings.fundTotalContributions[k] = fundTotals.fundTotalContributions;
		holdings.fundTotalDistributions[k] = fundTotals.fundTotalDistributions;

		// Total class owned/shares outstanding (enables ownership % calculation in
		// income_allocations). Resolved via the same shared helper the per-class headline
		// template already uses (HeadlineFormatter.js's ownershipDecimal), rather than
		// reading equity[k].totalShares alone -- a second, independent resolution that can
		// silently disagree with the first. LLProducts' classes have totalOwned configured,
		// not totalShares, so reading totalShares alone always resolved to 0 for them: the
		// calculator's NAV math never engaged even though the headline's own ownership-
		// fraction math (classOwned / totalOwned) was already using the right denominator.
		const resolvedTotalShares = resolveClassTotalShares(k, { clientData, capTableData, client });
		if (resolvedTotalShares !== null) {
			holdings.totalClassOwned[k] = resolvedTotalShares;
		}

		// Only set denomination when explicitly configured or fund language is active.
		// When absent, each calculator uses its own method-specific fallback.
		const explicitDenom = clientData?.equity?.[k]?.denomination;
		if (explicitDenom) {
			holdings.denomination[k] = explicitDenom;
		} else if (clientData?.use_fund_language) {
			holdings.denomination[k] = 'dollars';
		}

		// Strike price
		if (options.zeroStrikePrice) {
			holdings.strikePrice[k] = 0;
		} else {
			holdings.strikePrice[k] = hasStrikePriceType
				? getEffectiveStrikePriceForUser(equityClass, userIds, {
						useWeighted: true,
						netContra: true,
						outstandingOnly: true,
					})
				: 0;
		}
	});

	// Convert TVPI to raw NAV if client uses TVPI-style valuations
	const useTvpi = isTvpiValuation(clientData);
	if (useTvpi) {
		convertTvpiToNav(holdings, shareClasses, clientData.equity);
	}

	return {
		holdings,
		shareClasses,
		options: {
			floorAtZero: options.floorAtZero || false,
			percentagePrecision: useTvpi ? 10 : (options.percentagePrecision ?? clientData.flags?.percentage_precision ?? 4),
			useFundLanguage: clientData.use_fund_language || false,
		},
	};
}

// computeCAGRAnchors and attachCAGR are re-exported from ./cagr-utils.js at the
// top of this file. Kept there (not here) so calculators can import them
// without pulling in this module's clientData/string-utils dependency chain.
